import { useCallback, useState } from "react";
import {
  activateRoute as activateRouteRequest,
  activateVoyage as activateVoyageRequest,
  deleteRoute as deleteRouteRequest,
  deleteVoyage as deleteVoyageRequest,
  updateRoute as updateRouteRequest,
  updateVoyage as updateVoyageRequest,
} from "@/services/shippingDbApi";
import {
  isRouteSelected,
} from "@/features/routeTree/state/VesselTreeSelectors.jsx";
import { treeSelectionActions } from "@/features/routeTree/state/VesselTreeState.jsx";

/**
 * useRouteTreeActions
 *
 * Purpose:
 * - Manage route action menu and delete workflow state.
 * - Encapsulate candidate discard and persisted route delete behavior.
 */
export default function useRouteTreeActions({
  accessContext,
  setAccessContext,
  treeState,
  treeDispatch,
  removeRouteFromVoyage,
  upsertRouteForVoyage,
  archiveOtherActivePlannedRoutes,
  archivePlannedRoutesForVoyage,
  upsertVoyageForVessel,
  removeVoyageForVessel,
  archiveOtherActiveVoyages,
  onDiscardDraft,
  onRenameDraft,
  onRouteDeleted,
  onVoyageDeleted,
  onRouteStateChanged,
}) {
  const [routeDeleteCandidate, setRouteDeleteCandidate] = useState(null);
  const [routeDeleteOpen, setRouteDeleteOpen] = useState(false);
  const [routeActionAnchorEl, setRouteActionAnchorEl] = useState(null);
  const [voyageActionAnchorEl, setVoyageActionAnchorEl] = useState(null);
  const [voyageActionTarget, setVoyageActionTarget] = useState(null);
  const [voyageDeleteCandidate, setVoyageDeleteCandidate] = useState(null);
  const [voyageDeleteOpen, setVoyageDeleteOpen] = useState(false);
  const [routeActionTarget, setRouteActionTarget] = useState(null);
  const [actionError, setActionError] = useState("");

  const openDeleteDialog = useCallback((vessel, voyage, route) => {
    const voyageId = Number(voyage?.id);
    const isCandidateRoute = route?.source === "draft";
    const routeId = Number(route?.id);
    if (!Number.isFinite(voyageId)) return;
    if (!isCandidateRoute && !Number.isFinite(routeId)) return;
    setRouteDeleteCandidate({ vessel, voyage, route });
    setRouteDeleteOpen(true);
  }, []);

  const handleOpenRouteActionMenu = useCallback((event, vessel, voyage, route) => {
    event.stopPropagation();
    setRouteActionAnchorEl(event.currentTarget);
    setRouteActionTarget({ vessel, voyage, route });
  }, []);

  const handleCloseRouteActionMenu = useCallback(() => {
    setRouteActionAnchorEl(null);
  }, []);

  const handleRouteActionMenuExited = useCallback(() => {
    setRouteActionTarget(null);
  }, []);

  const handleDeleteRouteConfirm = useCallback(async () => {
    const candidate = routeDeleteCandidate;
    if (!candidate) return;
    const { vessel, voyage, route } = candidate;
    const isCandidateRoute = route?.source === "draft";
    const routeId = Number(route?.id);
    const voyageId = Number(voyage?.id);
    if (
      !Number.isFinite(voyageId) ||
      (!isCandidateRoute && !Number.isFinite(routeId))
    ) {
      setRouteDeleteOpen(false);
      return;
    }

    try {
      if (isCandidateRoute) {
        if (isRouteSelected(treeState, route?.id)) {
          treeDispatch(
            treeSelectionActions.toggleRouteSelected(
              vessel?.id,
              voyageId,
              route?.id,
            ),
          );
        }
        onDiscardDraft?.(route);
        setRouteDeleteOpen(false);
        return;
      }

      await deleteRouteRequest({
        accessContext,
        setAccessContext,
        routeId,
      });

      onRouteDeleted?.({
        routeId,
        routeName: route?.name ?? `Route ${routeId}`,
      });

      removeRouteFromVoyage(voyageId, routeId);

      if (isRouteSelected(treeState, routeId)) {
        treeDispatch(
          treeSelectionActions.toggleRouteSelected(
            vessel?.id,
            voyageId,
            routeId,
          ),
        );
      }
    } catch (err) {
      console.error("Failed to delete route:", err);
      alert(`Delete failed: ${err?.message ?? "Unknown error"}`);
    } finally {
      setRouteDeleteOpen(false);
    }
  }, [
    routeDeleteCandidate,
    treeState,
    treeDispatch,
    onDiscardDraft,
    accessContext,
    setAccessContext,
    onRouteDeleted,
    removeRouteFromVoyage,
  ]);

  // 'active' goes through activateRoute (demotes the active sibling); others patch.
  const handleSetRouteState = useCallback(
    async (target, nextState) => {
      const { voyage, route } = target ?? {};
      const routeId = Number(route?.id);
      const voyageId = Number(voyage?.id);
      if (
        route?.source === "draft" ||
        !Number.isFinite(routeId) ||
        !Number.isFinite(voyageId)
      )
        return;

      const activating = nextState === "active";
      try {
        const updated = activating
          ? await activateRouteRequest({
              accessContext,
              setAccessContext,
              routeId,
            })
          : await updateRouteRequest({
              accessContext,
              setAccessContext,
              routeId,
              patch: { state: nextState },
            });
        // Mirror the DB's sibling demotion locally before the refetch.
        if (activating) {
          archiveOtherActivePlannedRoutes?.(voyageId, routeId);
        }
        upsertRouteForVoyage?.(voyageId, {
          ...route,
          ...(updated && typeof updated === "object" ? updated : {}),
          state: nextState,
          active: nextState === "active",
        });
        onRouteStateChanged?.();
      } catch (err) {
        console.error("Failed to set route state:", err);
        setActionError(err?.message ?? "Action failed.");
      }
    },
    [
      accessContext,
      setAccessContext,
      upsertRouteForVoyage,
      archiveOtherActivePlannedRoutes,
      onRouteStateChanged,
    ],
  );

  const handleRenameRoute = useCallback(
    async (target, nextName) => {
      const { voyage, route } = target ?? {};
      const name = String(nextName ?? "").trim();
      if (!name) return;

      // Drafts have no DB row: patch the store instead of the backend.
      if (route?.source === "draft") {
        onRenameDraft?.(route, name);
        return;
      }

      const routeId = Number(route?.id);
      const voyageId = Number(voyage?.id);
      if (!Number.isFinite(routeId) || !Number.isFinite(voyageId)) return;

      const updated = await updateRouteRequest({
        accessContext,
        setAccessContext,
        routeId,
        patch: { name },
      });
      upsertRouteForVoyage?.(voyageId, {
        ...route,
        ...(updated && typeof updated === "object" ? updated : {}),
        name,
      });
    },
    [accessContext, setAccessContext, upsertRouteForVoyage, onRenameDraft],
  );

  const handleOpenVoyageActionMenu = useCallback((event, vessel, voyage) => {
    event.stopPropagation();
    setVoyageActionAnchorEl(event.currentTarget);
    setVoyageActionTarget({ vessel, voyage });
  }, []);

  const handleCloseVoyageActionMenu = useCallback(() => {
    setVoyageActionAnchorEl(null);
    setVoyageActionTarget(null);
  }, []);

  const openVoyageDeleteDialog = useCallback((vessel, voyage) => {
    if (!Number.isFinite(Number(voyage?.id))) return;
    setVoyageDeleteCandidate({ vessel, voyage });
    setVoyageDeleteOpen(true);
  }, []);

  const handleDeleteVoyageConfirm = useCallback(async () => {
    const candidate = voyageDeleteCandidate;
    if (!candidate) return;
    const { vessel, voyage } = candidate;
    const vesselId = Number(vessel?.id ?? voyage?.vesselId);
    const voyageId = Number(voyage?.id);
    if (!Number.isFinite(voyageId)) {
      setVoyageDeleteOpen(false);
      return;
    }

    try {
      await deleteVoyageRequest({ accessContext, setAccessContext, voyageId });

      onVoyageDeleted?.({
        voyageId,
        voyageName: voyage?.name ?? `Voyage ${voyageId}`,
      });

      removeVoyageForVessel(vesselId, voyageId);
    } catch (err) {
      console.error("Failed to delete voyage:", err);
      alert(`Delete failed: ${err?.message ?? "Unknown error"}`);
    } finally {
      setVoyageDeleteOpen(false);
    }
  }, [
    voyageDeleteCandidate,
    accessContext,
    setAccessContext,
    onVoyageDeleted,
    removeVoyageForVessel,
  ]);

  // 'active' goes through activateVoyage (demotes the active sibling); others patch.
  const handleSetVoyageState = useCallback(
    async (target, nextState) => {
      const { vessel, voyage } = target ?? {};
      const vesselId = Number(vessel?.id ?? voyage?.vesselId);
      const voyageId = Number(voyage?.id);
      if (!Number.isFinite(voyageId)) return;

      const activating = nextState === "active";
      try {
        const updated = activating
          ? await activateVoyageRequest({
              accessContext,
              setAccessContext,
              voyageId,
            })
          : await updateVoyageRequest({
              accessContext,
              setAccessContext,
              voyageId,
              patch: { state: nextState },
            });
        // Mirror the DB's sibling demotion locally before the refetch.
        if (activating && Number.isFinite(vesselId)) {
          archiveOtherActiveVoyages?.(vesselId, voyageId);
        }
        if (nextState === "archived") {
          archivePlannedRoutesForVoyage?.(voyageId);
        }
        if (Number.isFinite(vesselId)) {
          upsertVoyageForVessel?.(vesselId, {
            ...voyage,
            ...(updated && typeof updated === "object" ? updated : {}),
            state: nextState,
            active: nextState === "active",
          });
        }
        onRouteStateChanged?.();
      } catch (err) {
        console.error("Failed to set voyage state:", err);
        setActionError(err?.message ?? "Action failed.");
      }
    },
    [
      accessContext,
      setAccessContext,
      upsertVoyageForVessel,
      archiveOtherActiveVoyages,
      archivePlannedRoutesForVoyage,
      onRouteStateChanged,
    ],
  );

  return {
    routeDeleteCandidate,
    routeDeleteOpen,
    setRouteDeleteOpen,
    setRouteDeleteCandidate,
    routeActionAnchorEl,
    routeActionTarget,
    openDeleteDialog,
    handleOpenRouteActionMenu,
    handleCloseRouteActionMenu,
    handleRouteActionMenuExited,
    handleDeleteRouteConfirm,
    handleSetRouteState,
    handleRenameRoute,
    voyageActionAnchorEl,
    voyageActionTarget,
    handleOpenVoyageActionMenu,
    handleCloseVoyageActionMenu,
    handleSetVoyageState,
    voyageDeleteCandidate,
    voyageDeleteOpen,
    setVoyageDeleteOpen,
    setVoyageDeleteCandidate,
    openVoyageDeleteDialog,
    handleDeleteVoyageConfirm,
    actionError,
    setActionError,
  };
}
